import React from 'react';
import { motion } from 'framer-motion';
import { Award, Globe, Briefcase, Target, CheckCircle } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const About = () => {
  const expertise = [
    {
      icon: Award,
      title: 'Food Safety & Quality',
      description: 'FSSC 22000, ISO 22000, HACCP, BRCGS, IFS implementation and auditing expertise.'
    },
    {
      icon: Globe,
      title: 'International Experience',
      description: 'Over 20 years working across India, Middle East, and Europe with diverse stakeholders.'
    },
    {
      icon: Briefcase,
      title: 'Supply Chain Mastery',
      description: 'Deep understanding of food value chain operations and optimization strategies.'
    },
    {
      icon: Target,
      title: 'Strategic Growth',
      description: 'Proven track record in helping businesses achieve operational excellence and growth.'
    }
  ];

  return (
    <div className="about-page" style={{ backgroundColor: 'var(--color-cream)' }}>
      <SEO
        title="About Us"
        path="/about"
        description="Meet the founder of Prime Food & Financial Consultancy — a food industry professional with 20+ years of international experience across India, the Middle East, and Europe in food safety, quality management, and financial services."
        keywords="about Prime Food Financial Consultancy, food industry expert Netherlands, food safety consultant, 20 years experience"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" style={{ backgroundColor: 'var(--color-navy)' }}>
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  whileInView={{ scale: [0.8, 1.05, 1] }}
                  transition={{ duration: 0.6 }}
                  className="inline-block mb-4 px-4 py-2 rounded-full"
                  style={{ backgroundColor: 'var(--color-gold)', color: 'var(--color-navy)' }}
                >
                  <span className="text-sm font-semibold tracking-wide">FOUNDER & LEAD CONSULTANT</span>
                </motion.div>
                <h1 className="font-serif text-6xl md:text-7xl font-bold mb-6" style={{ color: 'var(--color-gold)' }}>
                  About Our Founder
                </h1>
                <p className="text-xl md:text-2xl leading-relaxed" style={{ color: 'var(--color-cream)', opacity: 0.9 }}>
                  A food industry professional with over 20 years of international experience across India, the Middle East, and Europe.
                </p>
              </motion.div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative h-[500px] overflow-hidden"
              >
                <img
                  src="https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg"
                  alt="Founder & Lead Consultant"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent"
                  style={{ background: 'linear-gradient(to top, rgba(10, 24, 40, 0.8), transparent)' }}
                />
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <h2 className="font-serif text-5xl md:text-6xl font-bold mb-8 text-center" style={{ color: 'var(--color-navy)' }}>
                My Story
              </h2>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="space-y-6 text-lg leading-relaxed" style={{ color: 'var(--color-dark-gray)' }}>
                <p>
                  Throughout my career, I have had the privilege of working in diverse markets and gaining valuable expertise in <strong style={{ color: 'var(--color-navy)' }}>supply chain operations</strong>, <strong style={{ color: 'var(--color-navy)' }}>food safety and quality management</strong>, and <strong style={{ color: 'var(--color-navy)' }}>food innovation</strong>.
                </p>
                <p>
                  My professional journey has provided me with a deep understanding of the challenges businesses face in maintaining quality standards, ensuring regulatory compliance, optimizing operations, and creating sustainable growth. I have worked with different stakeholders across the food value chain, helping organizations improve processes, enhance efficiency, and achieve excellence.
                </p>
                <p>
                  With my extensive industry knowledge and practical experience, I am committed to supporting businesses and individuals through reliable, customized consultancy solutions. At <strong style={{ color: 'var(--color-emerald)' }}>Prime Food & Financial Consultancy</strong>, my goal is to help customers achieve success by providing expert guidance in food consultancy, compliance, operational improvement, and financial services.
                </p>
                <p>
                  I believe in building long-term partnerships based on <strong style={{ color: 'var(--color-navy)' }}>trust</strong>, <strong style={{ color: 'var(--color-navy)' }}>professionalism</strong>, and delivering solutions that create real value.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="section">
        <div className="container">
          <AnimatedSection>
            <h2 className="font-serif text-5xl md:text-6xl font-bold mb-16 text-center" style={{ color: 'var(--color-navy)' }}>
              Areas of Expertise
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {expertise.map((item, index) => {
              const Icon = item.icon;
              return (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ y: -10, boxShadow: '0 20px 50px rgba(0,0,0,0.1)' }}
                    className="bg-white p-8 border-l-4 h-full"
                    style={{ borderLeftColor: 'var(--color-emerald)' }}
                  >
                    <div className="p-3 rounded-full inline-block mb-4" style={{ backgroundColor: 'var(--color-emerald)' }}>
                      <Icon size={32} style={{ color: 'white' }} />
                    </div>
                    <h3 className="font-serif text-2xl font-semibold mb-3" style={{ color: 'var(--color-navy)' }}>
                      {item.title}
                    </h3>
                    <p className="text-lg" style={{ color: 'var(--color-dark-gray)' }}>
                      {item.description}
                    </p>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <AnimatedSection>
        <section className="py-20" style={{ backgroundColor: 'var(--color-emerald)' }}>
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <motion.h2
                whileInView={{ scale: [0.9, 1] }}
                className="font-serif text-5xl font-bold mb-12 text-center text-white"
              >
                Core Values
              </motion.h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { title: 'Trust', description: 'Building relationships based on honesty and reliability' },
                  { title: 'Excellence', description: 'Delivering the highest quality in every engagement' },
                  { title: 'Partnership', description: 'Working together towards shared success' }
                ].map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.15 }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-navy)' }}
                    >
                      <CheckCircle size={32} style={{ color: 'var(--color-gold)' }} />
                    </div>
                    <h3 className="font-serif text-2xl font-semibold mb-2 text-white">{value.title}</h3>
                    <p className="text-white/90">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Certifications Section */}
      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto">
              <h2 className="font-serif text-5xl font-bold mb-8 text-center" style={{ color: 'var(--color-navy)' }}>
                Certifications & Standards
              </h2>
              <p className="text-xl text-center mb-12" style={{ color: 'var(--color-dark-gray)' }}>
                Expert knowledge and implementation experience in:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {['FSSC 22000', 'ISO 22000', 'HACCP', 'BRCGS', 'IFS', 'EU Regulations', 'GMP', 'Food Safety'].map((cert, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white p-6 text-center border-2 shadow-md"
                    style={{ borderColor: 'var(--color-emerald)' }}
                  >
                    <Award size={32} className="mx-auto mb-2" style={{ color: 'var(--color-emerald)' }} />
                    <span className="font-semibold" style={{ color: 'var(--color-navy)' }}>{cert}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <AnimatedSection>
        <section className="py-24" style={{ backgroundColor: 'var(--color-navy)' }}>
          <div className="container text-center">
            <motion.h2
              whileInView={{ scale: [0.9, 1] }}
              className="font-serif text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-gold)' }}
            >
              Let's Work Together
            </motion.h2>
            <motion.p
              whileInView={{ opacity: [0, 1] }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-10 max-w-2xl mx-auto" style={{ color: 'var(--color-cream)', opacity: 0.9 }}
            >
              Ready to transform your business with expert consultancy? Let's start a conversation.
            </motion.p>
            <motion.div
              whileInView={{ y: [30, 0], opacity: [0, 1] }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                >
                  <span>Get in Touch</span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default About;
